import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../../environment/environment';
import {
  V2BulkRequest,
  V2ExtraPickupRequest,
  V2FeedbackRequest,
  V2FeedbackResponse,
  V2ServiceChangeRequest,
  V2BulkResponseUnion,
  V2ExtraPickupResponseUnion,
  V2ServiceChangeResponseUnion,
} from '../republic.service.type';

@Injectable({
  providedIn: 'root',
})
export class KmtService {
  private readonly KMT_ASK_ENDPOINT = '/kmt/internal/kmt';

  private readonly FEEDBACK_ENDPOINT = '/feedback/feedback';

  constructor(private http: HttpClient) {}

  private generateRequestId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for test environments using secure UUID v4 generation
    const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
    let result = '';
    for (let i = 0; i < template.length; i += 1) {
      const char = template[i];
      if (char === 'x') {
        result += Math.floor(Math.random() * 16).toString(16);
      } else if (char === 'y') {
        result += (Math.floor(Math.random() * 4) + 8).toString(16);
      } else {
        result += char;
      }
    }
    return result;
  }

  private getUsername(): string {
    try {
      const token = localStorage.getItem('Authorization');
      if (token) {
        const payloadPart = token.split('.')[1];
        const decodedPayload = atob(payloadPart);
        const parsedToken = JSON.parse(decodedPayload);
        return parsedToken.preferred_username || parsedToken.name || 'unknown';
      }
    } catch (error) {
      console.error('Error extracting username from token:', error);
    }
    return 'unknown';
  }

  private createHeaders(): HttpHeaders {
    const username = this.getUsername();
    const requestId = this.generateRequestId();
    const authToken = localStorage.getItem('Authorization');

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': environment.v2_kmt_api_key,
      'x-username': username,
      'x-request-id': requestId,
    });

    // Add Authorization header if it exists
    if (authToken) {
      headers = headers.set('Authorization', authToken);
    }

    console.log('KMT Headers being sent:', {
      'Content-Type': 'application/json',
      'x-api-key': '[REDACTED]',
      'x-username': username,
      'x-request-id': requestId,
      Authorization: authToken ? 'Bearer [REDACTED]' : 'None',
    });

    return headers;
  }

  getBulkItems(
    polygon: string,
    division: string,
    lawson: string,
    items: string[],
  ): Observable<V2BulkResponseUnion> {
    const requestBody: V2BulkRequest = {
      parameters: {
        channel: 'internal',
        topic: 'bulk_item',
        polygon,
        division,
        lawson,
        items,
      },
    };

    const headers = this.createHeaders();
    console.log('KMT Bulk Request Headers:', headers.keys());
    console.log('KMT Bulk Request URL:', `${environment.v2_endpoint}${this.KMT_ASK_ENDPOINT}`);

    return this.http.post<V2BulkResponseUnion>(
      `${environment.v2_endpoint}${this.KMT_ASK_ENDPOINT}`,
      requestBody,
      {
        headers,
        context: undefined, // Prevent any context-based headers
      },
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('KMT Bulk Request Error:', error);
        console.error('Error Status:', error.status);
        console.error('Error Message:', error.message);
        console.error('Error Headers:', error.headers);
        console.error('Full Error Object:', JSON.stringify(error, null, 2));
        return throwError(() => error);
      }),
    );
  }

  getExtraPickupItems(
    polygon: string,
    division: string,
    lawson: string,
    services: string[],
  ): Observable<V2ExtraPickupResponseUnion> {
    const requestBody: V2ExtraPickupRequest = {
      parameters: {
        channel: 'internal',
        topic: 'extra_pickup',
        polygon,
        division,
        lawson,
        service: services,
      },
    };

    const headers = this.createHeaders();

    return this.http.post<V2ExtraPickupResponseUnion>(
      `${environment.v2_endpoint}${this.KMT_ASK_ENDPOINT}`,
      requestBody,
      { headers },
    );
  }

  sendFeedback(feedbackRequest: V2FeedbackRequest): Observable<V2FeedbackResponse> {
    const username = this.getUsername();
    const requestId = this.generateRequestId();
    const authToken = localStorage.getItem('Authorization');

    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('x-api-key', environment.v2_feedback_api_key)
      .set('x-username', username)
      .set('x-request-id', requestId);

    // Add Authorization header if it exists
    if (authToken) {
      headers = headers.set('Authorization', authToken);
    }

    return this.http.post<V2FeedbackResponse>(
      `${environment.v2_endpoint}${this.FEEDBACK_ENDPOINT}`,
      feedbackRequest,
      { headers },
    );
  }

  getServiceChange(
    polygon: string,
    division: string,
    lawson: string,
  ): Observable<V2ServiceChangeResponseUnion> {
    const requestBody: V2ServiceChangeRequest = {
      parameters: {
        channel: 'internal',
        topic: 'service_change',
        polygon,
        division,
        lawson,
      },
    };

    const headers = this.createHeaders();
    console.log('KMT Service Change Request Headers:', headers.keys());
    console.log('KMT Service Change Request URL:', `${environment.v2_endpoint}${this.KMT_ASK_ENDPOINT}`);

    return this.http.post<V2ServiceChangeResponseUnion>(
      `${environment.v2_endpoint}${this.KMT_ASK_ENDPOINT}`,
      requestBody,
      {
        headers,
        context: undefined,
      },
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('KMT Service Change Request Error:', error);
        console.error('Error Status:', error.status);
        console.error('Error Message:', error.message);
        console.error('Error Headers:', error.headers);
        console.error('Full Error Object:', JSON.stringify(error, null, 2));
        return throwError(() => error);
      }),
    );
  }
}
